<script setup lang="ts">
import type { SearchFormTypeSelectorProperties, TripType } from '@optionway/models';
import { Check } from 'lucide-vue-next';

const prefix = 'components.search.formTypeSelector';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.searchFormTypeSelector;

const properties = defineProps<SearchFormTypeSelectorProperties>();

const tripType = defineModel<TripType>({ default: 'ROUND_TRIP' });

const componentConfig = computed<SearchFormTypeSelectorProperties>(() => {
  return {
    ignoredTripType: properties.ignoredTripType ?? config?.ignoredTripType,
  };
});

const searchCriteria = useSearchCriteriaStore();
const { tripTypeList } = storeToRefs(searchCriteria);

const availableTripTypeList = computed<TripType[]>(() =>
  tripTypeList.value.filter((tripTypeItem) => !componentConfig.value.ignoredTripType?.includes(tripTypeItem)),
);

const isOpen = ref(false);
</script>

<template>
  <!-- Mobile: full-width tabs -->
  <div class="mb-2 flex border-b md:hidden">
    <UiButton
      v-for="tripTypeItem in availableTripTypeList"
      :key="tripTypeItem"
      :aria-pressed="tripType === tripTypeItem"
      variant="tertiary"
      class="min-w-0 flex-1 justify-center whitespace-normal rounded-none text-xs hover:bg-transparent sm:text-sm"
      :class="
        tripType === tripTypeItem
          ? 'border-b-2 border-brand-primary-700 text-brand-primary-700'
          : 'border-b-2 border-transparent text-secondary-foreground'
      "
      @click="tripType = tripTypeItem"
    >
      {{ $t(`${prefix}.${tripTypeItem}`) }}
    </UiButton>
  </div>

  <!-- Desktop: dropdown -->
  <div class="hidden md:block">
    <UiPopover v-model:open="isOpen">
      <UiPopoverTrigger
        class="rounded-md pb-1 pt-2.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary-700"
      >
        <UiCustomCollapsibleButtonTrigger
          :expanded="isOpen"
          :label="$t(`${prefix}.${tripType}`)"
          label-class="text-text-secondary-600"
          icon-class="text-text-secondary-600"
        />
      </UiPopoverTrigger>
      <UiPopoverContent
        align="start"
        :side-offset="0"
        class="w-56 rounded-md p-1"
      >
        <div
          role="menu"
          class="flex flex-col gap-1"
        >
          <button
            v-for="(aTripType, index) in availableTripTypeList"
            :key="`${aTripType}-${index}`"
            role="menuitemradio"
            :aria-checked="aTripType === tripType"
            class="flex w-full cursor-pointer select-none items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:text-inherit focus-visible:ring-2 focus-visible:ring-brand-primary-700"
            :class="aTripType === tripType ? 'bg-brand-primary-50' : ''"
            @click="
              tripType = aTripType;
              isOpen = false;
            "
          >
            <span :class="aTripType === tripType ? 'font-semibold text-text-primary-900' : ''">
              {{ $t(`${prefix}.${aTripType}`) }}
            </span>
            <Check
              v-if="aTripType === tripType"
              aria-hidden="true"
              class="text-brand-primary-700"
            />
          </button>
        </div>
      </UiPopoverContent>
    </UiPopover>
  </div>
</template>
